import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { EAccountRole, PatchAccountBody } from 'types/GlobalAccount'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

import { useAccount, useRoles } from 'redux/hooks/Accounts'
import { useTranslation } from 'language/translator'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import { useGroups } from 'redux/hooks/Groups'
import { formatGroupOption } from 'utils/functions'
import SelectUserRole from 'components_new/organisms/SelectUserRole'
import Box from 'components_new/atoms/Box'
import UserAvatar from 'components_new/molecules/UserAvatar'
import SectionHeader from 'components_new/molecules/SectionHeader'
import {
  useDefaultOrganizationalUnits,
  useFullAccessEnum,
  useOrganizationalUnitsArray
} from 'redux/hooks/OrganizationalUnits'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import InputAdornment from 'components_new/atoms/InputAdornment'
import Icon from 'components_new/atoms/Icon'
import { useAllowInternalFollowUp } from 'redux/hooks/Customer'

interface EditUserDialogProps {
  groups: Option[]
  id: string
  onClose: () => void
  open: boolean
  updateUser: (body: PatchAccountBody) => void
}

const EditUserDialog = (props: EditUserDialogProps) => {
  const { groups, id, onClose, open, updateUser } = props

  const { t, lang } = useTranslation()

  // get group options
  const { allGroups } = useGroups()
  const groupOptions = useMemo(() => {
    return allGroups.map((group) => formatGroupOption(group, t))
  }, [allGroups])
  const organizationalUnits = useOrganizationalUnitsArray()
  const defaultOrganizationalUnits = useDefaultOrganizationalUnits()
  const fullAccessEnum = useFullAccessEnum()

  const [updatedFirstName, setUpdatedFirstName] = useState<string>('')
  const [updatedLastName, setUpdatedLastName] = useState<string>('')
  const [selectedGroups, setSelectedGroups] = useState<Option[]>([])
  const [updatedRole, setUpdatedRoleState] = useState<EAccountRole>(
    EAccountRole.VIEW
  )
  const [organizationalUnitId, setOrganizationalUnitId] =
    useState<string>(fullAccessEnum)
  const [allowEconomy, setAllowEconomy] = useState(false)
  const [allowAnalyses, setAllowAnalyses] = useState(false)

  const user = useAccount(id)
  const { allowAccess, isCompanyGroup } = useAccountSettings()
  const { roles, loading: loadingRoles } = useRoles()
  const allowInternalFollowUp = useAllowInternalFollowUp()

  useEffect(() => {
    if (!user) return

    setAllowEconomy(user.has_access_to_economy_settings)
    setAllowAnalyses(user.allow_access_to_analyses)
    setUpdatedFirstName(user.first_name ?? '')
    setUpdatedLastName(user.last_name ?? '')
    setUpdatedRole(user.role)
    setSelectedGroups(groups)

    setOrganizationalUnitId(user.organizational_unit_id || fullAccessEnum)
  }, [user])

  const handleClose = () => {
    onClose()
    setUpdatedFirstName('')
    setUpdatedLastName('')
    setUpdatedRole(EAccountRole.VIEW)
    setSelectedGroups([])
    setOrganizationalUnitId(fullAccessEnum)
  }

  const setUpdatedRole = (newRole: EAccountRole) => {
    if (newRole === EAccountRole.ADMIN) {
      setOrganizationalUnitId(fullAccessEnum)
    }

    setUpdatedRoleState(newRole)
  }

  const handleSubmit = () => {
    if (updatedFirstName && updatedLastName) {
      updateUser({
        data: {
          first_name: updatedFirstName,
          groups: selectedGroups.map((g) => g.id) as string[],
          last_name: updatedLastName,
          role: roles[updatedRole].id,
          has_access_to_economy_settings: allowEconomy,
          allow_access_to_analyses: allowAnalyses,
          organizational_unit_id:
            organizationalUnitId === fullAccessEnum
              ? null
              : organizationalUnitId
        }
      })

      onClose()
    }
  }

  if (!user || loadingRoles) {
    return null
  }

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={handleClose}>
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={!Boolean(updatedFirstName && updatedLastName)}
              variant="contained"
              onClick={handleSubmit}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={handleClose}
        open={open}
        title={t('users', 'edit_user')}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, mb: 2 }}>
          <UserAvatar
            email={user.email}
            name={`${updatedFirstName} ${updatedLastName}`}
            size={'xlarge'}
            variant={'user'}
          />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                error={!Boolean(updatedFirstName)}
                fullWidth
                helperText={
                  !Boolean(updatedFirstName)
                    ? t('users', 'first_name_missing_error')
                    : undefined
                }
                label={t('users', 'first_name')}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setUpdatedFirstName(event.target.value)
                }}
                value={updatedFirstName ?? ''}
              />
              <TextField
                error={!Boolean(updatedLastName)}
                fullWidth
                helperText={
                  !Boolean(updatedLastName)
                    ? t('users', 'last_name_missing_error')
                    : undefined
                }
                label={t('users', 'last_name')}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setUpdatedLastName(event.target.value)
                }}
                value={updatedLastName ?? ''}
              />
            </Box>

            <TextField
              disabled
              fullWidth
              label={t('users', 'email')}
              value={user.email}
            />
          </Box>
        </Box>

        <SectionHeader
          divider={true}
          gutterBottom={true}
          title={t('global', 'access')}
        />

        <SelectUserRole
          allowEconomy={allowEconomy}
          allowAnalyses={allowAnalyses}
          disabled={
            user.email.includes('@stockholm.se') ||
            user.email.includes('@stockholmtest.se') ||
            user.company_group_id !== null
          }
          role={updatedRole}
          setAllowEconomy={setAllowEconomy}
          setAllowAnalyses={setAllowAnalyses}
          setRole={setUpdatedRole}
          showAnalyses={allowAccess.allow_analyses}
        />

        {allowInternalFollowUp && !user.company_group_id ? (
          <TextField
            disabled={updatedRole === EAccountRole.ADMIN}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="Access" />
                </InputAdornment>
              )
            }}
            label={t('data_access', 'data_access')}
            select={true}
            value={organizationalUnitId || fullAccessEnum}
            onChange={(e) => setOrganizationalUnitId(e.target.value)}
          >
            {defaultOrganizationalUnits.map((unit) => (
              <MenuItem key={unit.id} value={unit.id}>
                {unit.name[lang]}
              </MenuItem>
            ))}

            {organizationalUnits.map((unit) => {
              return (
                <MenuItem value={unit.id} key={unit.id}>
                  {unit.name}
                </MenuItem>
              )
            })}
          </TextField>
        ) : null}

        {allowInternalFollowUp && !user.company_group_id ? (
          <SelectSearch
            disabled={isCompanyGroup}
            label={t('global', 'groups')}
            multiple={true}
            selected={selectedGroups}
            onChange={(options: Option[] | null) =>
              options ? setSelectedGroups(options) : setSelectedGroups([])
            }
            options={groupOptions}
          />
        ) : null}
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default EditUserDialog
